import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box, Grid, TextField, Typography, Button, Divider, MenuItem, Select,
    FormControl, InputLabel, Paper, CircularProgress, Snackbar, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Tooltip, Chip, Pagination, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import api, { baseURL } from '../../services/api';
import * as qs from 'qs';
import { maskCNPJ } from '../../utils/mask';
import Assinatura from '../../assets/ass.png';
import { useAuth } from '../../contexts/auth.context';

const createEmptyForm = (emitterDoc = '') => ({
    payerName: '',
    payerDoc: '',
    referente: '',
    value: '',
    emitterDoc,
    emitterName: '',
    emitterPhone: '',
    emitterCity: '',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    observations: '',
});

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function formatCurrency(val) {
    const num = parseFloat(val.toString().replace(/[^\d,]/g, '').replace(',', '.'));
    if (isNaN(num)) return val;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseCurrencyNumber(value) {
    if (value === null || value === undefined || value === '') return NaN;
    const normalized = value.toString().replace(/[^\d,.-]/g, '').replace(',', '.');
    return parseFloat(normalized);
}

function numberToPortugueseWords(n) {
    const units = ['zero', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const teens = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const tens = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const hundreds = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
    const scales = [
        { singular: '', plural: '' },
        { singular: 'mil', plural: 'mil' },
        { singular: 'milhão', plural: 'milhões' },
        { singular: 'bilhão', plural: 'bilhões' },
        { singular: 'trilhão', plural: 'trilhões' },
    ];

    const groupToWords = (num) => {
        if (num === 0) return '';
        if (num === 100) return 'cem';

        const h = Math.floor(num / 100);
        const d = Math.floor((num % 100) / 10);
        const u = num % 10;
        const parts = [];

        if (h > 0) parts.push(hundreds[h]);
        if (d === 1) {
            parts.push(teens[u]);
        } else {
            if (d > 1) parts.push(tens[d]);
            if (u > 0) parts.push(units[u]);
        }

        return parts.join(' e ');
    };

    if (n === 0) return 'zero';

    const groups = [];
    let value = n;
    while (value > 0) {
        groups.push(value % 1000);
        value = Math.floor(value / 1000);
    }

    const parts = [];
    for (let i = groups.length - 1; i >= 0; i -= 1) {
        const groupValue = groups[i];
        if (groupValue === 0) continue;

        const scale = scales[i] || scales[scales.length - 1];
        const words = groupToWords(groupValue);

        if (i === 1) {
            parts.push(groupValue === 1 ? 'mil' : `${words} mil`);
        } else if (i > 1) {
            parts.push(`${words} ${groupValue === 1 ? scale.singular : scale.plural}`);
        } else {
            parts.push(words);
        }
    }

    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

function currencyToWords(value) {
    const num = parseCurrencyNumber(value);
    if (Number.isNaN(num)) return '';

    const absolute = Math.abs(num);
    let inteiro = Math.floor(absolute);
    let centavos = Math.round((absolute - inteiro) * 100);

    if (centavos === 100) {
        inteiro += 1;
        centavos = 0;
    }

    const inteiroWords = numberToPortugueseWords(inteiro);
    const inteiroLabel = inteiro === 1 ? 'real' : 'reais';
    const centavosWords = centavos > 0 ? numberToPortugueseWords(centavos) : '';
    const centavosLabel = centavos === 1 ? 'centavo' : 'centavos';

    let result = `${inteiroWords} ${inteiroLabel}`;
    if (centavos > 0) {
        result += ` e ${centavosWords} ${centavosLabel}`;
    }

    if (num < 0) {
        result = `menos ${result}`;
    }

    return result;
}

export default function Recibos() {
    const [form, setForm] = useState(createEmptyForm());
    const [searchingEmitter, setSearchingEmitter] = useState(false);
    const [searchingPayer, setSearchingPayer] = useState(false);
    const [saving, setSaving] = useState(false);
    const { basePath } = useAuth();
    const [alert, setAlert] = useState({ open: false, type: 'success', msg: '' });
    const receiptRef = useRef();
    const defaultEmitterDoc = useRef('');
    const [configLogoUrl, setConfigLogoUrl] = useState('');

    // Lista de recibos salvos
    const [receipts, setReceipts] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [pages, setPages] = useState({ pageCount: 1 });
    const currentPage = useRef(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
    const [payerSearchModalOpen, setPayerSearchModalOpen] = useState(false);
    const [payerSearchName, setPayerSearchName] = useState('');
    const [payerSearchResults, setPayerSearchResults] = useState([]);
    const [payerSearchLoading, setPayerSearchLoading] = useState(false);
    const [payerSearchAttempted, setPayerSearchAttempted] = useState(false);
    const [lockCriticalFields, setLockCriticalFields] = useState(false);

    // Metadados exibidos no recibo (ID e data de criação)
    const receiptNumber = useRef('');
    const receiptCreatedAt = useRef('');

    const handleField = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const resolveAbsoluteUrl = (url) => {
        if (!url) return '';
        if (/^https?:\/\//i.test(url)) return url;
        const root = baseURL || window.location.origin;
        return `${root}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const getMediaUrl = (mediaField) => (
        mediaField?.data?.attributes?.url || mediaField?.url || ''
    );

    const extractConfigAttributes = (responseData) => (
        responseData?.data?.attributes
        || responseData?.attributes
        || {}
    );

    const getCompanyByCNPJ = async (doc) => {
        const query = qs.stringify({
            filters: { document: { $eq: doc } },
            populate: '*',
            encodeValuesOnly: true,
        });
        const response = await api.get('/companies?' + query);
        const data = response.data.data;
        if (data && data.length > 0) {
            return data[0].attributes;
        }
        return null;
    };

    const searchEmitterByCNPJ = async (doc, { silent = false } = {}) => {
        setSearchingEmitter(true);
        try {
            const company = await getCompanyByCNPJ(doc);
            if (company) {
                setForm(prev => ({
                    ...prev,
                    emitterName: company.social_reason || company.trade || '',
                    emitterPhone: company.phone || '',
                    emitterCity: company.city || '',
                }));
                if (!silent) {
                    setAlert({ open: true, type: 'success', msg: 'Dados do emissor preenchidos automaticamente.' });
                }
            } else if (!silent) {
                setAlert({ open: true, type: 'warning', msg: 'CNPJ do emissor não encontrado no cadastro.' });
            }
        } catch (err) {
            if (!silent) {
                setAlert({ open: true, type: 'error', msg: 'Erro ao buscar CNPJ do emissor.' });
            }
        } finally {
            setSearchingEmitter(false);
        }
    };

    useEffect(() => {
        const loadDefaultConfig = async () => {
            try {
                let response;
                try {
                    response = await api.get('/config/1?populate=*');
                } catch {
                    response = await api.get('/configs/1?populate=*');
                }

                const attributes = extractConfigAttributes(response.data);
                const documentFromConfig = maskCNPJ(
                    attributes.document || attributes.cnpj || attributes.emitter_doc || ''
                );
                const emitterNameFromConfig = attributes.social_reason || attributes.trade || attributes.emitter_name || '';
                const emitterPhoneFromConfig = attributes.phone || attributes.emitter_phone || '';
                const emitterCityFromConfig = attributes.city || attributes.emitter_city || '';
                const logoFromConfig = resolveAbsoluteUrl(
                    getMediaUrl(attributes.logo)
                    || getMediaUrl(attributes.logoRecibo)
                    || getMediaUrl(attributes.logo_receipt)
                    || getMediaUrl(attributes.receiptLogo)
                    || getMediaUrl(attributes.logoHeader)
                );

                setForm(prev => ({
                    ...prev,
                    emitterName: prev.emitterName || emitterNameFromConfig,
                    emitterPhone: prev.emitterPhone || emitterPhoneFromConfig,
                    emitterCity: prev.emitterCity || emitterCityFromConfig,
                }));

                if (documentFromConfig) {
                    defaultEmitterDoc.current = documentFromConfig;
                    setForm(prev => ({ ...prev, emitterDoc: documentFromConfig }));
                    searchEmitterByCNPJ(documentFromConfig, { silent: true });
                }

                if (logoFromConfig) {
                    setConfigLogoUrl(logoFromConfig);
                }
            } catch (err) {
                console.error('Erro ao carregar configuração padrão de recibos:', err);
            }
        };

        loadDefaultConfig();
        // Executa apenas no carregamento da tela.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEmitterCNPJChange = (e) => {
        const formatted = maskCNPJ(e.target.value);
        setForm(prev => ({ ...prev, emitterDoc: formatted }));
        if (formatted.replace(/\D/g, '').length === 14) {
            searchEmitterByCNPJ(formatted);
        }
    };

    const handlePayerCNPJChange = (e) => {
        const formatted = maskCNPJ(e.target.value);
        setForm(prev => ({ ...prev, payerDoc: formatted }));
    };

    const openPayerSearchModal = () => {
        setPayerSearchModalOpen(true);
        setPayerSearchAttempted(false);
        setPayerSearchResults([]);
        setPayerSearchName(form.payerName || '');
    };

    const handleSearchPayerByName = async () => {
        const searchTerm = payerSearchName.trim();

        if (searchTerm.length < 2) {
            setAlert({ open: true, type: 'warning', msg: 'Digite ao menos 2 caracteres para pesquisar por nome.' });
            return;
        }

        setPayerSearchLoading(true);
        setPayerSearchAttempted(true);
        try {
            const query = qs.stringify({
                filters: {
                    $or: [
                        { social_reason: { $containsi: searchTerm } },
                        { trade: { $containsi: searchTerm } },
                    ],
                },
                sort: ['social_reason:asc'],
                pagination: { page: 1, pageSize: 20 },
                encodeValuesOnly: true,
            });

            const response = await api.get('/companies?' + query);
            setPayerSearchResults(response.data.data || []);
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao pesquisar empresas por nome.' });
            setPayerSearchResults([]);
        } finally {
            setPayerSearchLoading(false);
        }
    };

    const handleSelectPayerFromModal = (company) => {
        const attributes = company?.attributes || {};
        setForm(prev => ({
            ...prev,
            payerName: attributes.social_reason || attributes.trade || '',
            payerDoc: maskCNPJ(attributes.document || prev.payerDoc),
        }));
        setPayerSearchModalOpen(false);
        setAlert({ open: true, type: 'success', msg: 'Dados do pagador preenchidos automaticamente.' });
    };

    const handleSearchPayerByCNPJ = async () => {
        if (lockCriticalFields) return;

        if (form.payerDoc.replace(/\D/g, '').length !== 14) {
            openPayerSearchModal();
            return;
        }

        setSearchingPayer(true);
        try {
            const company = await getCompanyByCNPJ(form.payerDoc);
            if (company) {
                setForm(prev => ({
                    ...prev,
                    payerName: company.social_reason || company.trade || '',
                    payerDoc: maskCNPJ(company.document || prev.payerDoc),
                }));
                setAlert({ open: true, type: 'success', msg: 'Dados do pagador preenchidos automaticamente.' });
            } else {
                setAlert({ open: true, type: 'warning', msg: 'Empresa não encontrada para o CNPJ informado.' });
            }
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao buscar pagador pelo CNPJ.' });
        } finally {
            setSearchingPayer(false);
        }
    };

    // ─── Carregar lista de recibos ─── //
    const loadReceipts = useCallback(async (page = 1, search = '') => {
        setLoadingList(true);
        try {
            const filters = {};
            if (search.trim()) {
                filters.$or = [
                    { payer_name: { $containsi: search } },
                    { payer_doc: { $containsi: search } },
                    { emitter_name: { $containsi: search } },
                    { emitter_doc: { $containsi: search } },
                    { receipt_number: { $containsi: search } },
                ];
            }
            const query = qs.stringify({
                filters,
                sort: ['createdAt:desc'],
                pagination: { pageSize: 10, page },
                encodeValuesOnly: true,
            });
            const response = await api.get('/receipts?' + query);
            setReceipts(response.data.data || []);
            setPages(response.data.meta.pagination);
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao carregar recibos.' });
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => { loadReceipts(); }, [loadReceipts]);

    // ─── Salvar recibo ───
    const handleSave = async () => {
        const formToSave = {
            ...form,
            payerName: (form.payerName || '').trim(),
            referente: (form.referente || '').trim(),
            emitterName: (form.emitterName || '').trim(),
        };

        if (!formToSave.payerName && formToSave.payerDoc.replace(/\D/g, '').length === 14) {
            try {
                const company = await getCompanyByCNPJ(formToSave.payerDoc);
                if (company) {
                    formToSave.payerName = company.social_reason || company.trade || '';
                    setForm(prev => ({
                        ...prev,
                        payerName: formToSave.payerName || prev.payerName,
                        payerDoc: maskCNPJ(company.document || prev.payerDoc),
                    }));
                }
            } catch {
                // silencioso: validação final informa se ainda faltou campo
            }
        }

        if (!formToSave.emitterName && formToSave.emitterDoc.replace(/\D/g, '').length === 14) {
            try {
                const company = await getCompanyByCNPJ(formToSave.emitterDoc);
                if (company) {
                    formToSave.emitterName = company.social_reason || company.trade || '';
                    setForm(prev => ({
                        ...prev,
                        emitterName: formToSave.emitterName || prev.emitterName,
                        emitterPhone: company.phone || prev.emitterPhone,
                        emitterCity: company.city || prev.emitterCity,
                    }));
                }
            } catch {
                // silencioso: validação final informa se ainda faltou campo
            }
        }

        const missingFields = [];
        if (!formToSave.payerName) missingFields.push('Pagador');
        if (!String(formToSave.value || '').trim()) missingFields.push('Valor');
        if (!formToSave.referente) missingFields.push('Referente');
        if (!formToSave.emitterName) missingFields.push('Emissor');

        if (missingFields.length > 0) {
            setAlert({ open: true, type: 'warning', msg: `Preencha os campos obrigatórios: ${missingFields.join(', ')}.` });
            return;
        }
        setSaving(true);
        try {
            const internalReceiptNumber = `${Date.now()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
            const payload = {
                receipt_number: internalReceiptNumber,
                payer_name: formToSave.payerName,
                payer_doc: formToSave.payerDoc || null,
                referente: formToSave.referente,
                value: parseFloat(formToSave.value.toString().replace(/[^\d,]/g, '').replace(',', '.')) || 0,
                emitter_doc: formToSave.emitterDoc || null,
                emitter_name: formToSave.emitterName,
                emitter_phone: formToSave.emitterPhone || null,
                emitter_city: formToSave.emitterCity || null,
                payment_date: formToSave.paymentDate,
                observations: formToSave.observations || null,
            };
            const createdReceipt = await api.post('/receipts', { data: payload });
            const createdId = createdReceipt?.data?.data?.id;
            const createdAt = createdReceipt?.data?.data?.attributes?.createdAt;
            receiptNumber.current = createdId ? String(createdId) : '';
            receiptCreatedAt.current = createdAt || '';
            setAlert({
                open: true,
                type: 'success',
                msg: createdId ? `Recibo Nº ${createdId} salvo com sucesso!` : 'Recibo salvo com sucesso!'
            });
            await loadReceipts(currentPage.current, searchQuery);
            handleClear();
        } catch (err) {
            setAlert({ open: true, type: 'error', msg: 'Erro ao salvar recibo.' });
        } finally {
            setSaving(false);
        }
    };

    // ─── Carregar recibo salvo no formulário ───
    const handleLoad = (row, { autoPrint = false } = {}) => {
        const a = row.attributes;
        receiptNumber.current = String(row.id || '');
        receiptCreatedAt.current = a.createdAt || '';
        setForm({
            payerName: a.payer_name || '',
            payerDoc: a.payer_doc || '',
            referente: a.referente || '',
            value: a.value !== null && a.value !== undefined ? String(a.value).replace('.', ',') : '',
            emitterDoc: a.emitter_doc || '',
            emitterName: a.emitter_name || '',
            emitterPhone: a.emitter_phone || '',
            emitterCity: a.emitter_city || '',
            paymentDate: a.payment_date || format(new Date(), 'yyyy-MM-dd'),
            observations: a.observations || '',
        });
        setLockCriticalFields(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        if (autoPrint) {
            const printWindow = window.open('', '_blank', 'width=900,height=700');
            if (!printWindow) {
                setAlert({ open: true, type: 'warning', msg: 'Não foi possível abrir a janela de impressão.' });
                return;
            }
            setTimeout(() => handlePrint(printWindow), 250);
        }
    };

    // ─── Excluir recibo ───
    const handleDelete = async (id) => {
        try {
            const isCurrentReceiptOpen = String(receiptNumber.current || '') === String(id);
            await api.delete(`/receipts/${id}`);
            setAlert({ open: true, type: 'success', msg: 'Recibo excluído.' });
            if (isCurrentReceiptOpen) {
                handleClear();
            }
            await loadReceipts(currentPage.current, searchQuery);
        } catch {
            setAlert({ open: true, type: 'error', msg: 'Erro ao excluir recibo.' });
        } finally {
            setDeleteConfirm({ open: false, id: null });
        }
    };

    const handlePrint = (targetWindow = null) => {
        const printContents = receiptRef.current.innerHTML;
        const win = targetWindow || window.open('', '_blank', 'width=900,height=700');
        if (!win) {
            setAlert({ open: true, type: 'warning', msg: 'Não foi possível abrir a janela de impressão.' });
            return;
        }
        win.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8" />
                <title>Recibo</title>
                <style>
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    body { font-family: Arial, sans-serif; font-size: 13px; color: #222; background: #fff; }
                    .receipt-wrap { width: 190mm; min-height: 130mm; margin: 0 auto; padding: 12mm; }
                    .receipt-header { text-align: center; border-bottom: 2px solid #222; padding-bottom: 10px; margin-bottom: 14px; }
                    .receipt-header-top { display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; }
                    .receipt-logo { max-width: 170px; max-height: 70px; object-fit: contain; }
                    .receipt-header h2 { font-size: 22px; letter-spacing: 4px; text-transform: uppercase; }
                    .receipt-value-box { border: 2px solid #222; padding: 8px 18px; display: inline-block; font-size: 20px; font-weight: bold; float: right; margin-bottom: 8px; }
                    .receipt-body { line-height: 2; }
                    .receipt-body span.underline { border-bottom: 1px solid #555; display: inline-block; min-width: 260px; padding: 0 4px; }
                    .receipt-footer { margin-top: 34px; }
                    .signature-row { display: flex; gap: 24px; }
                    .signature-item { flex: 1; text-align: center; }
                    .signature-line { border-top: 1px solid #222; margin-bottom: 6px; }
                    .signature-label { font-size: 12px; }
                    .divider { border: none; border-top: 1px dashed #aaa; margin: 20px 0; }
                    .obs-block { margin-top: 12px; font-size: 12px; color: #444; }
                    .clearfix::after { content: ""; display: table; clear: both; }
                    @page { margin: 8mm; }
                    @media print { body { -webkit-print-color-adjust: exact; } }
                </style>
            </head>
            <body>${printContents}</body>
            </html>
        `);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); win.close(); }, 400);
    };

    const handleClear = () => {
        receiptNumber.current = '';
        receiptCreatedAt.current = '';
        setForm(createEmptyForm(defaultEmitterDoc.current));
        setLockCriticalFields(false);
        if (defaultEmitterDoc.current) {
            searchEmitterByCNPJ(defaultEmitterDoc.current, { silent: true });
        }
    };

    return (
        <Box sx={{ width: '100%', p: { xs: 2, md: 4 } }}>
            <Snackbar
                open={alert.open}
                autoHideDuration={3500}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setAlert(a => ({ ...a, open: false }))}
            >
                <Alert severity={alert.type} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>

            {/* Confirmar exclusão */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este recibo? Esta ação não pode ser desfeita.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>Excluir</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={payerSearchModalOpen} onClose={() => setPayerSearchModalOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Pesquisar Pagador por Nome</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Pesquise por razão social ou nome fantasia para preencher os dados do pagador.
                    </Typography>
                    <Box display="flex" gap={1.5} mb={2}>
                        <TextField
                            fullWidth
                            label="Razão Social / Nome Fantasia"
                            value={payerSearchName}
                            onChange={(e) => setPayerSearchName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchPayerByName();
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearchPayerByName}
                            startIcon={payerSearchLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                            disabled={payerSearchLoading}
                        >
                            Buscar
                        </Button>
                    </Box>

                    {payerSearchLoading ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={26} />
                        </Box>
                    ) : payerSearchResults.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Razão Social</TableCell>
                                        <TableCell>Nome Fantasia</TableCell>
                                        <TableCell>CNPJ</TableCell>
                                        <TableCell align="right">Ação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payerSearchResults.map((company) => (
                                        <TableRow key={company.id} hover>
                                            <TableCell>{company.attributes?.social_reason || '-'}</TableCell>
                                            <TableCell>{company.attributes?.trade || '-'}</TableCell>
                                            <TableCell>{maskCNPJ(company.attributes?.document || '') || '-'}</TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => handleSelectPayerFromModal(company)}>
                                                    Selecionar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : payerSearchAttempted ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhuma empresa encontrada para o nome informado.
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPayerSearchModalOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Typography variant="h5" fontWeight={700} mb={3}>Gerar Recibo</Typography>

            <Grid container spacing={4}>
                {/* ─── Formulário ─── */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Emissor</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="CNPJ do Emissor"
                                    value={form.emitterDoc}
                                    onChange={handleEmitterCNPJChange}
                                    placeholder="00.000.000/0001-00"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {searchingEmitter
                                                    ? <CircularProgress size={18} />
                                                    : <SearchIcon color="action" fontSize="small" />}
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Nome / Razão Social do Emissor" value={form.emitterName} onChange={handleField('emitterName')} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Telefone (opcional)" value={form.emitterPhone} onChange={handleField('emitterPhone')} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Cidade" value={form.emitterCity} onChange={handleField('emitterCity')} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Pagamento</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    label="CNPJ do Pagador"
                                    value={form.payerDoc}
                                    onChange={handlePayerCNPJChange}
                                    disabled={lockCriticalFields}
                                    placeholder="00.000.000/0001-00"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearchPayerByCNPJ();
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="primary"
                                    startIcon={searchingPayer ? <CircularProgress size={16} /> : <SearchIcon />}
                                    onClick={handleSearchPayerByCNPJ}
                                    disabled={searchingPayer || lockCriticalFields}
                                    sx={{ height: '100%' }}
                                >
                                    Buscar
                                </Button>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Pagador"
                                    value={form.payerName}
                                    onChange={handleField('payerName')}
                                    disabled={lockCriticalFields}
                                    helperText={
                                        lockCriticalFields
                                            ? 'Campo bloqueado após geração do recibo.'
                                            : 'Use o CNPJ e a lupa para carregar automaticamente a razão social.'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Valor (R$)"
                                    value={form.value}
                                    onChange={handleField('value')}
                                    disabled={lockCriticalFields}
                                    placeholder="0,00"
                                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                    helperText={lockCriticalFields ? 'Campo bloqueado após geração do recibo.' : ''}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="Referente a"
                                    value={form.referente}
                                    onChange={handleField('referente')}
                                    placeholder="Ex: prestação de serviços de consultoria referente ao mês de janeiro/2026"
                                />
                            </Grid>
                        
                            <Grid item xs={6}>
                                <TextField fullWidth type="date" label="Data do Pagamento" value={form.paymentDate} onChange={handleField('paymentDate')} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>Observações</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Texto livre / Observações"
                            value={form.observations}
                            onChange={handleField('observations')}
                            placeholder="Informe aqui observações adicionais que deseja incluir no recibo..."
                        />

                        <Box display="flex" gap={2} mt={3} flexWrap="wrap">
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={saving}
                                fullWidth
                            >
                                {saving ? 'Salvando...' : 'Salvar Recibo'}
                            </Button>
                            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} fullWidth>
                                Imprimir / PDF
                            </Button>
                            <Button variant="outlined" color="inherit" startIcon={<AddIcon />} onClick={handleClear} fullWidth>
                                Novo Recibo
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* ─── Preview do Recibo ─── */}
                <Grid item xs={12} md={7}>
                    <Box ref={receiptRef}>
                        <ReciboPrint
                            form={form}
                            receiptNumber={receiptNumber.current}
                            receiptCreatedAt={receiptCreatedAt.current}
                            logoUrl={configLogoUrl}
                            basePath={basePath}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* ─── Lista de Recibos Salvos ─── */}
            <Box mt={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                    <Typography variant="h6" fontWeight={700}>Recibos Salvos</Typography>
                    <TextField
                        size="small"
                        label="Buscar"
                        placeholder="Pagador, emissor, CNPJ ou nº recibo..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            currentPage.current = 1;
                            loadReceipts(1, e.target.value);
                        }}
                        InputProps={{
                            endAdornment: <InputAdornment position="end"><SearchIcon fontSize="small" color="action" /></InputAdornment>
                        }}
                        sx={{ width: 320 }}
                    />
                </Box>

                <TableContainer component={Paper} elevation={2}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Nº</strong></TableCell>
                                <TableCell><strong>Data</strong></TableCell>
                                <TableCell><strong>Pagador</strong></TableCell>
                                <TableCell><strong>Emissor</strong></TableCell>
                                <TableCell><strong>Referente</strong></TableCell>
                                <TableCell align="right"><strong>Valor</strong></TableCell>
                                <TableCell align="center"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingList ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : receipts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        Nenhum recibo encontrado
                                    </TableCell>
                                </TableRow>
                            ) : receipts.map((row) => {
                                const a = row.attributes;
                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>{a.createdAt ? format(new Date(a.createdAt), 'dd/MM/yyyy') : '-'}</TableCell>
                                        <TableCell>{a.payer_name}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap maxWidth={180}>{a.emitter_name}</Typography>
                                            {a.emitter_doc && <Typography variant="caption" color="text.secondary">{a.emitter_doc}</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap maxWidth={200}>{a.referente}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>R$ {formatCurrency(String(a.value ?? '0'))}</strong>
                                        </TableCell>                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0} justifyContent="center">
                                                <Tooltip title="Imprimir">
                                                    <IconButton color="primary" size="small" onClick={() => handleLoad(row, { autoPrint: true })}>
                                                        <PrintIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Editar recibo">
                                                    <IconButton color="info" size="small" onClick={() => handleLoad(row)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton color="error" size="small" onClick={() => setDeleteConfirm({ open: true, id: row.id })}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Stack direction="row" justifyContent="flex-end" mt={2}>
                    <Pagination
                        count={pages.pageCount || 1}
                        page={currentPage.current}
                        color="primary"
                        onChange={(_, value) => {
                            currentPage.current = value;
                            loadReceipts(value, searchQuery);
                        }}
                    />
                </Stack>
            </Box>
        </Box>
    );
}

function ReciboPrint({ form, receiptNumber, receiptCreatedAt, logoUrl, basePath}) {
    const formattedDate = receiptCreatedAt
        ? format(new Date(receiptCreatedAt), 'dd/MM/yyyy')
        : '__/__/____';

    const valorFormatado = form.value ? `R$ ${formatCurrency(form.value)}` : 'R$ _______________';
    const valorPorExtenso = form.value ? currencyToWords(form.value) : '';

    return (
        <Paper
            className="receipt-wrap"
            elevation={4}
            sx={{
                p: 4,
                borderRadius: 2,
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                width: '100%',
                maxWidth: '190mm',
                minHeight: '130mm',
                mx: 'auto',
            }}
        >
            {/* Cabeçalho */}
            <Box className="receipt-header" sx={{ textAlign: 'center', borderBottom: '2px solid #333', pb: 1.5, mb: 2 }}>
                <Box className="receipt-header-top" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {logoUrl && (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt="Logo da empresa"
                            className="receipt-logo"
                            sx={{ maxWidth: 180, maxHeight: 70, objectFit: 'contain' }}
                        />
                    )}
                    <Box className="receipt-header-text">
                        <Typography variant="h5" fontWeight={800} letterSpacing={4} textTransform="uppercase">
                            Recibo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Nº {receiptNumber}</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Valor em destaque */}
            <Box className="clearfix" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Box className="receipt-value-box" sx={{ border: '2px solid #333', px: 2.5, py: 1, fontWeight: 700, fontSize: '1.3rem' }}>
                    {valorFormatado}
                </Box>
            </Box>

            {/* Corpo */}
            <Box className="receipt-body" sx={{ lineHeight: 2.2 }}>
                <Typography variant="body1">
                    Recebi de{' '}
                    <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 220, px: 0.5, fontWeight: 600 }}>
                        {form.payerName || ''}
                    </Box>
                    {form.payerDoc && (
                        <>
                            {' '}portador do CPF/CNPJ{' '}
                            <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 140, px: 0.5 }}>
                                {form.payerDoc}
                            </Box>
                        </>
                    )}
                    {', '}a importância de{' '}
                    <Box component="span" sx={{display: 'inline-block', px: 1, fontWeight: 700 }}>
                        {valorFormatado}  {valorPorExtenso && `(${valorPorExtenso})`}
                    </Box>
                </Typography>

                

                <Typography variant="body1" mt={1}>
                    referente a{' '}
                    <Box component="span" sx={{ borderBottom: '1px solid #555', display: 'inline-block', minWidth: 300, px: 0.5 }}>
                        {form.referente || ''}
                    </Box>
                </Typography>


                {form.observations && (
                    <Box className="obs-block" sx={{ mt: 2, p: 1.5, backgroundColor: '#f9f9f9', borderRadius: 1, fontSize: '13px', color: '#444' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{form.observations}</Typography>
                    </Box>
                )}
            </Box>

            <Divider sx={{ borderStyle: 'dashed', my: 3 }} />

            {/* Emissor e data */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="body2">
                        Emissor:{' '}
                        <Box component="span" sx={{ fontWeight: 600 }}>{form.emitterName || '___________________________'}</Box>
                    </Typography>
                    {form.emitterDoc && (
                        <Typography variant="body2">CNPJ: {form.emitterDoc}</Typography>
                    )}
                    {form.emitterPhone && (
                        <Typography variant="body2">Tel.: {form.emitterPhone}</Typography>
                    )}
                </Box>
                <Box textAlign="right">
                    <Typography variant="body2">
                        {form.emitterCity || '_________________'}, {formattedDate}
                    </Typography>
                </Box>
            </Box>

            {/* Assinaturas */}
            <Box className="receipt-footer" sx={{ mt: 5 }}>
                <Box className="signature-row" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box className="signature-item" sx={{ flex: 1, minWidth: 220, alignContent: 'center', textAlign: 'center' }}>
                        <Typography className="signature-label" variant="caption" display="block">
                           { basePath  === 'apeprev' ? (
                        <>
                            <img src={Assinatura} alt="Assinatura" width={140} style={{ "transform": "rotate(22deg)", "margin-top": '-20px' }} />
                            <Typography variant="body2" sx={{ marginTop: -4, textDecoration: "overline" }}>
                                Presidente da Apeprev
                            </Typography>
                        </>
                    ) : null}
                        </Typography>
                        <br />
                        <Typography variant="caption" display="block">{form.emitterName || ''}</Typography>
                        {form.emitterDoc && (
                            <Typography variant="caption" display="block">{form.emitterDoc}</Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
}
